import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  Utensils,
  Heart,
  ArrowDown,
  Newspaper,
  Sparkles,
  Award,
  Quote,
  ArrowRight,
  Pause,
  Play,
  ArrowLeftRight,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

import type { ImpactData, StudentStory, ThankYouMessage, ThankYouWallResponse, AuthorityTestimonial } from '../types';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import DonationForm from '../components/donation/DonationForm';
import MediaCoverage from '../components/donation/MediaCoverage';
import EventSchedule from '../components/donation/EventSchedule';
import AnimatedCounter from '../components/shared/AnimatedCounter';
import GrainOverlay from '../components/shared/GrainOverlay';

const HERMES_EASE = [0.22, 1, 0.36, 1] as const;

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: HERMES_EASE },
  },
};

// Hero 禮炮效果 — 明顯彩色紙片爆發（360度向四周發射）
const CONFETTI_COLORS = ['#fbbf24', '#f97316', '#ef4444', '#3b82f6', '#10b981', '#ec4899', '#8b5cf6', '#fde047', '#fb923c'];

function HeroConfetti() {
  const particles = useMemo(() => {
    return Array.from({ length: 50 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 50 + (Math.random() - 0.5) * 0.4;
      const dist = 60 + Math.random() * 200;
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 40,
        size: 8 + Math.random() * 12,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        delay: Math.random() * 0.4,
        duration: 1.2 + Math.random() * 1.2,
        rotate: Math.random() * 720 - 360,
        shape: i % 5 === 0 ? 'star' : Math.random() > 0.6 ? 'circle' : 'square',
      };
    });
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 30, overflow: 'visible' }}>
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute"
          style={{
            left: '50%',
            top: '35%',
            marginLeft: -p.size / 2,
            marginTop: -p.size / 2,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
          animate={{
            x: p.x,
            y: p.y,
            opacity: [0, 1, 1, 0],
            scale: [0, 1.2, 1, 0.3],
            rotate: p.rotate,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {p.shape === 'star' ? (
            <svg width={p.size} height={p.size} viewBox="0 0 24 24" fill={p.color}>
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          ) : (
            <div
              style={{
                width: p.size,
                height: p.size,
                borderRadius: p.shape === 'circle' ? '50%' : '2px',
                backgroundColor: p.color,
                boxShadow: `0 0 ${Math.round(p.size * 0.8)}px ${p.color}80`,
              }}
            />
          )}
        </motion.div>
      ))}
    </div>
  );
}

// Hero 逐行動畫 — 差異化定位版本
const heroLines = [
  { text: '別人送的是一餐溫飽', delay: 0.5 },
  { text: '我們送的是一個人生轉捩點', delay: 2.0 },
  { text: '$50 = 一餐飯 + 一份職訓', delay: 3.5, highlight: true },
  { text: '一份善心，兩份改變', delay: 5.0, highlight: true },
];

// 純圖片翻轉組件（無按鈕，無狀態）
function FlipImage({
  front,
  back,
  name,
  isFlipped,
}: {
  front?: string;
  back?: string;
  name: string;
  isFlipped: boolean;
}) {
  // If no illustration, just show photo
  if (!back) {
    return (
      <div className="w-full h-full bg-brand-100 overflow-hidden">
        {front ? (
          <img src={front} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
            <Award className="w-12 h-12 text-orange-300" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full relative" style={{ perspective: '1200px' }}>
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front: student photo */}
        <div
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          {front ? (
            <div className="w-full h-full bg-brand-100">
              <img src={front} alt={name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
              <Award className="w-12 h-12 text-orange-300" />
            </div>
          )}
        </div>
        {/* Back: illustration */}
        <div
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div className="w-full h-full bg-brand-100">
            <img src={back} alt={`${name} 插畫`} className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </div>
  );
}

// 學員故事卡片 — 獨立組件管理翻轉狀態
function StoryCard({
  story,
  index,
}: {
  story: StudentStory;
  index: number;
}) {
  const [isFlipped, setIsFlipped] = useState(false);

  // 翻轉後 5 秒自動返回
  useEffect(() => {
    if (!isFlipped) return;
    const timer = setTimeout(() => {
      setIsFlipped(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isFlipped]);

  return (
    <motion.div
      className="card overflow-hidden hover:shadow-xl transition-shadow duration-300"
      initial={{ opacity: 0, x: -60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{
        duration: 0.8,
        ease: HERMES_EASE,
        delay: index * 0.1,
      }}
    >
      {/* 照片區域 — 始終正方形 */}
      <div className="aspect-square">
        <FlipImage
          front={story.photoUrl}
          back={story.illustrationUrl}
          name={story.name}
          isFlipped={isFlipped}
        />
      </div>

      {/* 翻轉按鈕 — 只有正面顯示，翻轉後 5 秒自動返回 */}
      {story.illustrationUrl && !isFlipped && (
        <div className="px-6 pt-4">
          <button
            onClick={() => setIsFlipped(true)}
            className="mx-auto flex items-center gap-1.5 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all animate-glow-pulse"
          >
            <ArrowLeftRight className="w-4 h-4" />
            實踐啟發 💡
          </button>
        </div>
      )}

      <div className="p-6">
        <h3 className="font-bold text-lg text-gray-900 mb-2">
          {story.name}
        </h3>
        {story.jobRole && (
          <p className="text-sm text-brand-600 font-medium mb-2">
            {story.jobRole}
          </p>
        )}
        <p className="text-sm text-gray-600 leading-relaxed line-clamp-4">
          {story.storyText}
        </p>
        {story.quote && (
          <blockquote className="mt-3 text-sm italic text-gray-500 border-l-2 border-brand-200 pl-3">
            "{story.quote}"
          </blockquote>
        )}
      </div>
    </motion.div>
  );
}

export default function DonationLanding() {
  const [impact, setImpact] = useState<ImpactData | null>(null);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [fundraisingGoal, setFundraisingGoal] = useState<number>(1000000);
  const [stories, setStories] = useState<StudentStory[]>([]);
  const [testimonials, setTestimonials] = useState<AuthorityTestimonial[]>([]);
  const [wallMessages, setWallMessages] = useState<ThankYouMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroLineIndex, setHeroLineIndex] = useState(0);
  const [heroSkipped, setHeroSkipped] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonialPaused, setTestimonialPaused] = useState(false);
  const [testimonialExpanded, setTestimonialExpanded] = useState(false);

  // 數據加載
  useEffect(() => {
    (async () => {
      const [impactRes, storiesRes, testimonialsRes, wallRes, settingsRes] = await Promise.all([
        apiFetch<ImpactData>('/api/public/impact'),
        apiFetch<StudentStory[]>('/api/public/stories'),
        apiFetch<AuthorityTestimonial[]>('/api/public/testimonials'),
        apiFetch<ThankYouWallResponse>('/api/public/thank-you-wall'),
        apiFetch<Record<string, string>>('/api/public/settings'),
      ]);
      if (impactRes.success && impactRes.data) setImpact(impactRes.data);
      if (storiesRes.success && storiesRes.data) setStories(storiesRes.data);
      if (testimonialsRes.success && testimonialsRes.data) setTestimonials(testimonialsRes.data);
      if (wallRes.success && wallRes.data) setWallMessages(wallRes.data.messages || []);
      if (settingsRes.success && settingsRes.data) {
        setSettings(settingsRes.data);
        if (settingsRes.data.fundraisingGoal) {
          setFundraisingGoal(Number(settingsRes.data.fundraisingGoal));
        }
      }
      setLoading(false);
    })();
  }, []);

  // Hero 逐行動畫
  useEffect(() => {
    if (heroSkipped) return;
    const timers = heroLines.map((line, i) =>
      setTimeout(() => setHeroLineIndex(i + 1), line.delay * 1000)
    );
    return () => timers.forEach(clearTimeout);
  }, [heroSkipped]);

  // 背書人自動輪播
  useEffect(() => {
    if (testimonials.length === 0 || testimonialPaused) return;
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, testimonialPaused]);

  const scrollToStories = () => {
    setHeroSkipped(true);
    setHeroLineIndex(heroLines.length);
    document.getElementById('stories')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-cream">
      <GrainOverlay />
      <Navbar />

      {/* ===== Hero Section ===== */}
      <section
        className="min-h-[calc(100vh-64px)] relative flex flex-col items-center justify-center text-white px-4 overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(/images/photo-kitchen-training.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <motion.div
          className="text-center max-w-3xl mx-auto relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Differentiation badge */}
          <motion.div
            variants={fadeInUp}
            className="mb-6"
          >
            <span
              className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-sm font-medium text-orange-200"
              style={{
                textShadow: '0 0 8px rgba(251, 146, 60, 0.6), 0 0 20px rgba(251, 146, 60, 0.3), 0 0 40px rgba(251, 146, 60, 0.15)',
              }}
            >
              <Sparkles className="w-4 h-4" />
              {settings.heroBadge || '不只是送飯 — 我們在改寫人生'}
            </span>
          </motion.div>

          {/* Animated lines */}
          <div className="min-h-[200px] mb-6">
            <AnimatePresence mode="wait">
              {heroLines.slice(0, heroLineIndex).map((line, i) => (
                <motion.p
                  key={i}
                  className={`text-xl sm:text-2xl md:text-3xl font-medium mb-3 ${
                    line.highlight
                      ? 'text-yellow-300 font-extrabold text-2xl sm:text-3xl md:text-4xl'
                      : 'text-orange-100'
                  }`}
                  initial={{ opacity: 0, y: 20, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{ duration: 0.8, ease: HERMES_EASE }}
                >
                  {line.text}
                </motion.p>
              ))}
            </AnimatePresence>
            {heroLineIndex === 0 && !heroSkipped && (
              <motion.p
                className="text-xl sm:text-2xl text-orange-200/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                ...
              </motion.p>
            )}
          </div>

          {/* Main title (always visible after animation or skip) */}
          {(heroLineIndex >= heroLines.length || heroSkipped) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: HERMES_EASE }}
              className="relative"
            >
              {/* Confetti burst */}
              <HeroConfetti />
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 leading-tight">
                <span className="block">一餐飯，</span>
                <span className="block">改寫兩個人生</span>
              </h1>
              <p className="text-lg sm:text-xl text-orange-100 mb-8 max-w-2xl mx-auto leading-relaxed">
                {settings.heroSubtitle || '$50 = 1餐改變生命的飯'}
              </p>
            </motion.div>
          )}

          <motion.div variants={fadeInUp}>
            <a
              href="#donate"
              className="inline-flex items-center gap-2 bg-white text-brand-700 font-bold py-4 px-10 rounded-2xl hover:bg-brand-50 transition-all min-h-[56px] text-lg shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
            >
              <Heart className="w-5 h-5 fill-brand-600 text-brand-600" />
              立即捐款
            </a>
          </motion.div>
        </motion.div>

        {/* Skip button */}
        {!heroSkipped && heroLineIndex < heroLines.length && (
          <motion.button
            className="absolute bottom-20 right-6 text-orange-200/70 text-sm hover:text-white transition-colors flex items-center gap-1 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            onClick={() => setHeroSkipped(true)}
          >
            跳過 →
          </motion.button>
        )}

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6, ease: HERMES_EASE }}
        >
          <button onClick={scrollToStories} className="text-orange-200 hover:text-white transition-colors">
            <ArrowDown className="w-6 h-6 animate-bounce" />
          </button>
        </motion.div>
      </section>

      {/* ===== 差異化價值主張區 ===== */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: HERMES_EASE }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <Sparkles className="w-4 h-4" />
              {settings.leverageBadge || '善的槓桿'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {settings.leverageTitle || '一份捐款，兩份改變'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {settings.leverageDescription || '傳統食物援助只解決「今天吃什麼」，我們同時解決「明天靠什麼」。這不是單純的施捨，而是創造可以自我循環的社會價值。'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Card 1: 基層飯餐 */}
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-lg group"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: HERMES_EASE }}
            >
              <div className="h-56 md:h-72 overflow-hidden">
                <img
                  src="/images/photo-elderly-dining.jpg"
                  alt="基層人士享用飯餐"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)' }}>
                <div className="text-sm font-bold text-orange-300 mb-1">{settings.leverageCard1Label || '第一重改變'}</div>
                <h3 className="text-xl font-bold mb-2">{settings.leverageCard1Title || '溫飽基層家庭'}</h3>
                <p className="text-sm text-white/90 leading-relaxed">
                  {settings.leverageCard1Body || '為獨居長者、低收入家庭、殘障人士提供營養均衡的飯餐，讓他們不必在「吃飽」與「吃好」之間做選擇。'}
                </p>
              </div>
            </motion.div>

            {/* Card 2: SEN 職訓 */}
            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-lg group"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: HERMES_EASE, delay: 0.15 }}
            >
              <div className="h-56 md:h-72 overflow-hidden">
                <img
                  src="/images/photo-students-preparing.jpg"
                  alt="SEN 青年職訓"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)' }}>
                <div className="text-sm font-bold text-yellow-300 mb-1">{settings.leverageCard2Label || '第二重改變'}</div>
                <h3 className="text-xl font-bold mb-2">{settings.leverageCard2Title || '培育 SEN 青年自立'}</h3>
                <p className="text-sm text-white/90 leading-relaxed">
                  {settings.leverageCard2Body || '讓自閉症、過度活躍症等 SEN 青年在真實職場中學習餐飲技能，從「被照顧者」變成「能貢獻者」。'}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Leverage arrow */}
          <motion.div
            className="flex items-center justify-center mt-8"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 bg-orange-50 border border-orange-200 rounded-2xl sm:rounded-full px-5 sm:px-6 py-4 sm:py-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">傳統慈善：</span>
                <span className="text-sm font-bold text-gray-400 line-through">送飯 → 吃飽</span>
              </div>
              <ArrowRight className="w-4 h-4 text-orange-400 hidden sm:block" />
              <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center sm:hidden">
                <ArrowRight className="w-3 h-3 text-orange-500 rotate-90" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">好餸傳愛：</span>
                <span className="text-sm font-bold text-orange-700">送飯 → 吃飽 + 職訓 + 就業</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== 影響力數據區 ===== */}
      <section className="section-padding bg-orange-50">
        <div className="container-max mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: HERMES_EASE }}
          >
            <h2 className="text-[29px] sm:text-[35px] font-bold text-gray-900 mb-3 leading-snug">
              <span className="block sm:inline">{settings.impactTitle || '你的每分捐獻，'}</span>
              <span className="block sm:inline">都在改寫劇本 🎬</span>
            </h2>
            <div className="text-gray-600 max-w-xl mx-auto text-[17px] md:text-[22px] space-y-1">
              <div className="flex justify-center">
                <div className="flex flex-col items-start sm:flex-row sm:gap-4 text-left">
                  <div className="flex items-start gap-1.5">
                    <span className="text-brand-400 mt-1">✨</span>
                    <span className="inline-block w-10 md:w-12">目標</span>
                    <span className="font-bold text-brand-600">HK${fundraisingGoal.toLocaleString()}</span>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <span className="text-brand-400 mt-1">✨</span>
                    <span className="inline-block w-10 md:w-12">限期</span>
                    <span className="font-bold text-brand-600">{settings.fundraisingEndDate || '2026年6月30日'}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-500">這場改變，缺你不可 👇</p>
            </div>
          </motion.div>
          {(() => {
            const impactCards = [
              {
                label: settings.impactLabelRaised || '已籌金額',
                target: impact ? Math.round(impact.totalDonated / 100) : 0,
                prefix: 'HK$',
                sub: impact ? `目標 HK$${fundraisingGoal.toLocaleString()}` : undefined,
                icon: TrendingUp,
                color: 'text-brand-600',
                bg: 'bg-brand-50',
                visible: true,
              },
              {
                label: settings.impactLabelDonors || '捐贈人數',
                target: impact && impact.totalDonors >= 30 ? impact.totalDonors : undefined,
                sub: impact && impact.totalDonors >= 30 ? `月捐 ${impact.monthlyDonors} 人` : undefined,
                icon: Users,
                color: 'text-blue-600',
                bg: 'bg-blue-50',
                visible: impact ? impact.totalDonors >= 30 : false,
              },
              {
                label: settings.impactLabelMeals || '送出飯餐',
                target: impact?.mealsProvided,
                sub: impact ? `培訓 ${impact.studentsSupported} 位學員` : undefined,
                icon: Utensils,
                color: 'text-green-600',
                bg: 'bg-green-50',
                visible: true,
              },
            ].filter((c) => c.visible);
            return (
              <div className={`grid grid-cols-1 ${impactCards.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-6`}>
                {impactCards.map((card, index) => (
              <motion.div
                key={card.label}
                className="card p-6 text-center"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.6,
                  ease: HERMES_EASE,
                  delay: index * 0.1,
                }}
              >
                <div
                  className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center mx-auto mb-4`}
                >
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
                <div className="text-3xl font-extrabold text-gray-900 mb-1">
                  {loading || card.target === undefined ? (
                    <span className="inline-block w-24 h-8 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <AnimatedCounter
                      target={card.target}
                      prefix={card.prefix}
                      duration={1.2}
                    />
                  )}
                </div>
                <div className="text-sm font-medium text-gray-500 mb-1">
                  {card.label}
                </div>
                {card.sub && (
                  <div className="text-xs text-gray-400">{card.sub}</div>
                )}
              </motion.div>
            ))}
              </div>
            );
          })()}

          {/* 進度條 */}
          {impact && (
            <motion.div
              className="mt-8 bg-white rounded-2xl p-6 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: HERMES_EASE, delay: 0.3 }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">籌款進度</span>
                <span className="text-sm font-bold text-brand-600">
                  {(() => {
                    const pct = impact ? impact.totalDonated / fundraisingGoal : 0;
                    return `${pct.toFixed(1).replace('.0', '')}%`;
                  })()}
                </span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-brand-500 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{
                    width: `${Math.min(impact.totalDonated / fundraisingGoal, 100)}%`,
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 1.2,
                    ease: HERMES_EASE,
                    delay: 0.5,
                  }}
                />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== 權威背書人區域 ===== */}
      <section className="section-padding bg-gradient-to-b from-orange-50 to-white">
        <div className="container-max mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: HERMES_EASE }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <Quote className="w-4 h-4" />
              {settings.testimonialsBadge || '權威見證'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {settings.testimonialsTitle || '他們看見的改變'}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              {settings.testimonialsDescription || '來自企業、教育界、文創界與餐飲業的領袖，親眼見證「一餐飯」的雙重力量'}
            </p>
          </motion.div>

          {/* Testimonial Cards */}
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden animate-pulse">
                <div className="grid md:grid-cols-[280px_1fr] gap-0">
                  <div className="h-64 md:h-auto bg-gray-200" />
                  <div className="p-6 md:p-8 space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-12" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ) : testimonials.length > 0 ? (
              <>
                {/* 改用純 opacity 淡入淡出，消除位移造成的 compositing layer 抖動 */}
                <div
                  className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden cursor-pointer min-h-[min(520px,70vh)] md:min-h-0"
                  style={{
                    contain: 'layout style paint',
                    isolation: 'isolate',
                  }}
                  onClick={() => setTestimonialPaused((p) => !p)}
                  title={testimonialPaused ? '點擊恢復輪播' : '點擊暫停輪播'}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTestimonial}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5, ease: HERMES_EASE }}
                    >
                      <div className="grid md:grid-cols-[280px_1fr] gap-0 h-full">
                        {/* Photo side */}
                        <div className="relative aspect-square md:aspect-auto md:h-full bg-gray-100">
                          <img
                            src={testimonials[activeTestimonial].photoUrl || '/images/placeholder-avatar.jpg'}
                            alt={testimonials[activeTestimonial].name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:bg-gradient-to-r" />
                        </div>
                        {/* Quote side */}
                        <div className="p-6 md:p-8 flex flex-col justify-center min-h-[220px] md:min-h-0">
                          <Quote className="w-8 h-8 text-orange-300 mb-4 hidden md:block" />
                          <p className={`text-gray-700 leading-relaxed text-base md:text-lg mb-2 ${testimonialExpanded ? '' : 'line-clamp-6'} md:line-clamp-none`}>
                            {testimonials[activeTestimonial].quote}
                          </p>
                          <button
                            onClick={(e) => { e.stopPropagation(); setTestimonialExpanded((v) => !v); }}
                            className="text-sm text-brand-600 font-medium hover:text-brand-700 underline md:hidden mb-4"
                          >
                            {testimonialExpanded ? '收起' : '繼續'}
                          </button>
                          <div className="mt-auto">
                            <div className="font-bold text-gray-900">
                              {testimonials[activeTestimonial].name}
                            </div>
                            <div className="text-sm text-brand-600 font-medium">
                              {testimonials[activeTestimonial].title}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {testimonials[activeTestimonial].organization}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Dots + Pause */}
                <div className="flex items-center justify-center gap-3 mt-6">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { setActiveTestimonial(i); setTestimonialPaused(true); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        i === activeTestimonial
                          ? 'bg-brand-600 w-8'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                  <button
                    onClick={() => setTestimonialPaused((p) => !p)}
                    className="ml-2 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
                    title={testimonialPaused ? '恢復輪播' : '暫停輪播'}
                  >
                    {testimonialPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Quote className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400">{settings.testimonialsEmpty || '權威見證即將上架，敬請期待'}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== 學員故事區 ===== */}
      <section id="stories" className="section-padding bg-white">
        <div className="container-max mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: HERMES_EASE }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <Sparkles className="w-4 h-4" />
              {settings.storiesBadge || '真實故事'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {settings.storiesTitle || '學員故事'}
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              {settings.storiesDescription || '每一位 SEN 青年都有自己的成長軌跡，社區飯堂為他們打開了一扇門'}
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-full mb-1" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : stories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stories.slice(0, 6).map((story, index) => (
                <StoryCard key={story.id} story={story} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">{settings.storiesEmpty || '學員故事即將上架，敬請期待'}</p>
            </div>
          )}
        </div>
      </section>

      {/* ===== 媒體報導區域 ===== */}
      <MediaCoverage />

      {/* ===== 捐款表單區 ===== */}
      <section id="donate" className="section-padding bg-orange-50">
        <div className="container-max mx-auto">
          <div className="max-w-xl mx-auto">
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: HERMES_EASE }}
            >
              <div className="inline-flex items-center gap-2 bg-white text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4 shadow-sm border border-orange-100">
                <Heart className="w-4 h-4 fill-orange-600 text-orange-600" />
                {settings.donateBadge || '立即行動'}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {settings.donateTitle || '立即支持'}
              </h2>
              <p className="text-gray-600">
                {settings.donateDescription || '每一分捐款，產生兩份改變的力量'}
              </p>
            </motion.div>
            <motion.div
              className="card p-6 sm:p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: HERMES_EASE, delay: 0.2 }}
            >
              <DonationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== 社區飯堂場次 ===== */}
      <EventSchedule />

      {/* ===== 感謝牆區 ===== */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: HERMES_EASE }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-bold mb-4">
              <Newspaper className="w-4 h-4" />
              {settings.wallBadge || '公開見證'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              {settings.wallTitle || '感謝牆'}
            </h2>
            <p className="text-gray-600">
              {settings.wallDescription || '感謝每一位同行者的善心'}
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card p-4 animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          ) : wallMessages.length > 0 ? (
            <div className="relative overflow-hidden" style={{ maxHeight: 560 }}>
              {/* 漸層遮罩 - 頂部 */}
              <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none" />
              {/* 漸層遮罩 - 底部 */}
              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
              
              <div className="wall-marquee-track">
                {/* 第一組 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {wallMessages.map((msg) => (
                    <div key={`a-${msg.id}`} className="card p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                          <Heart className="w-4 h-4 text-brand-600 fill-brand-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-sm text-gray-800 block truncate">
                            {msg.isAnonymous ? '善心人士' : msg.donorName}
                          </span>
                        </div>
                        {msg.badge && (
                          <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium whitespace-nowrap">
                            {msg.badge}
                          </span>
                        )}
                      </div>
                      {msg.message && (
                        <p className="text-sm text-gray-600 line-clamp-3">
                          {msg.message}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        {msg.amount && (
                          <p className="text-xs text-brand-600 font-medium">
                            捐款 HK${(msg.amount / 100).toLocaleString()}
                          </p>
                        )}
                        <p className="text-[10px] text-gray-400">
                          {new Date(msg.createdAt).toLocaleDateString('zh-HK')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* 第二組（無縫循環） */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {wallMessages.map((msg) => (
                    <div key={`b-${msg.id}`} className="card p-4 hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                          <Heart className="w-4 h-4 text-brand-600 fill-brand-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-sm text-gray-800 block truncate">
                            {msg.isAnonymous ? '善心人士' : msg.donorName}
                          </span>
                        </div>
                        {msg.badge && (
                          <span className="text-[10px] px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium whitespace-nowrap">
                            {msg.badge}
                          </span>
                        )}
                      </div>
                      {msg.message && (
                        <p className="text-sm text-gray-600 line-clamp-3">
                          {msg.message}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        {msg.amount && (
                          <p className="text-xs text-brand-600 font-medium">
                            捐款 HK${(msg.amount / 100).toLocaleString()}
                          </p>
                        )}
                        <p className="text-[10px] text-gray-400">
                          {new Date(msg.createdAt).toLocaleDateString('zh-HK')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400">{settings.wallEmpty || '暫無留言，成為第一個支持者吧！'}</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
